import { booheeCode, type ChineseFoodLookup } from './boohee'
import { workoutAppearanceSchema, type WorkoutAppearance } from './workoutAppearance'
import type { FoodPhotoRecognizer } from './foodPhoto'
import { createIntake, foodSchema, type Food, type IntakeInput } from './nutrition'
import { searchFoodCatalog } from './foodCatalog'
import { moodRecordSchema, periodDays, validateJournalDay, type MoodValue } from './journal'
import type { DateRange, FoodLookup, HealthDataProvider, ManualHealthDataStore } from './contracts'
import {
  buildMetricTrend,
  buildSleepTrend,
  selectTodaySnapshot,
  summarizeWorkoutWeek,
  type MetricTrendPoint,
  type TodaySnapshot,
  type WorkoutWeekSummary,
} from './selectors'
import { localDayAt, mondayWeekRange, monthRange, parseIsoInstant } from './time'
import type {
  DataSource,
  ManualMetricInput,
  ManualSleepInput,
  ManualWorkoutInput,
  MetricRecord,
  MetricType,
  SleepRecord,
  WorkoutRecord,
} from './types'
import { parseMetricInput, parseSleepInput, parseWorkoutInput } from './validation'

const READ_ONLY_ERROR = '记录不存在或来自 Apple 健康'
// Bound trend allocation and range arithmetic to one year while allowing future views beyond 7/30 days.
const MAX_TREND_DAYS = 366

interface MergeableRecord {
  id: string
  source: DataSource
  externalId?: string
  updatedAt: string
}

interface CalendarDate {
  year: number
  month: number
  day: number
}

function recordKey(record: { id: string; source: DataSource; externalId?: string }): string {
  return record.source !== 'manual' && record.externalId
    ? `${record.source}:${record.externalId}`
    : `${record.source}:${record.id}`
}

function mergeRecords<T extends MergeableRecord>(groups: readonly (readonly T[])[]): T[] {
  const merged = new Map<string, T>()
  for (const records of groups) {
    for (const record of records) {
      const key = recordKey(record)
      const current = merged.get(key)
      if (
        current === undefined ||
        record.updatedAt > current.updatedAt ||
        (record.updatedAt === current.updatedAt && record.id > current.id)
      ) {
        merged.set(key, record)
      }
    }
  }
  return [...merged.values()]
}

function descendingByInstant<T extends { id: string }>(instant: (record: T) => string) {
  return (left: T, right: T): number => {
    const difference = parseIsoInstant(instant(right)).getTime() - parseIsoInstant(instant(left)).getTime()
    if (difference !== 0) return difference
    return right.id.localeCompare(left.id)
  }
}

const metricDescending = descendingByInstant<MetricRecord>((record) => record.recordedAt)
const sleepDescending = descendingByInstant<SleepRecord>((record) => record.endAt)
const workoutDescending = descendingByInstant<WorkoutRecord>((record) => record.startedAt)

function parseLocalDay(localDay: string): CalendarDate {
  const [year, month, day] = localDay.split('-').map(Number)
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error('无法解析本地日期')
  }
  return { year, month, day }
}

function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const result = new Date(Date.UTC(date.year, date.month - 1, date.day + days))
  return {
    year: result.getUTCFullYear(),
    month: result.getUTCMonth() + 1,
    day: result.getUTCDate(),
  }
}

function dateTimeParts(instant: Date, timeZone: string): Record<string, string> {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  })
  return Object.fromEntries(
    formatter
      .formatToParts(instant)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  )
}

function requiredPart(parts: Record<string, string>, name: string): number {
  const part = parts[name]
  if (part === undefined) throw new Error('无法解析本地日期')
  return Number(part)
}

// Each boundary is resolved independently so offset changes inside a range (DST) are preserved.
function localMidnightUtc(date: CalendarDate, timeZone: string): string {
  const targetWallTime = Date.UTC(date.year, date.month - 1, date.day)
  let candidate = targetWallTime
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = dateTimeParts(new Date(candidate), timeZone)
    const renderedWallTime = Date.UTC(
      requiredPart(parts, 'year'),
      requiredPart(parts, 'month') - 1,
      requiredPart(parts, 'day'),
      requiredPart(parts, 'hour'),
      requiredPart(parts, 'minute'),
      requiredPart(parts, 'second'),
    )
    const correction = targetWallTime - renderedWallTime
    candidate += correction
    if (correction === 0) break
  }
  return new Date(candidate).toISOString()
}

function localDayRange(
  referenceAt: string,
  timeZone: string,
  days: number,
): DateRange {
  if (!Number.isInteger(days) || days < 1 || days > MAX_TREND_DAYS) {
    throw new Error(`天数必须是 1 到 ${MAX_TREND_DAYS} 之间的整数`)
  }
  const today = parseLocalDay(localDayAt(referenceAt, timeZone))
  return {
    startInclusive: localMidnightUtc(addCalendarDays(today, 1 - days), timeZone),
    endExclusive: localMidnightUtc(addCalendarDays(today, 1), timeZone),
  }
}

export class HealthRepository {
  private readonly providers: readonly HealthDataProvider[]

  /**
   * Pass external/read-only providers in `providers`; the manual store is always added exactly once.
   * Supplying the same store in the array is tolerated and normalized by object identity.
   */
  constructor(
    providers: readonly HealthDataProvider[],
    private readonly manualStore: ManualHealthDataStore,
    private readonly foodLookup?: FoodLookup,
    private readonly foodPhoto?: FoodPhotoRecognizer,
    private readonly chineseFood?: ChineseFoodLookup,
  ) {
    this.providers = [...new Set([...providers.filter((provider) => provider !== manualStore), manualStore])]
  }

  async recognizeFoodPhoto(image: Blob, signal: AbortSignal) {
    if (!this.foodPhoto) throw new Error('演示模式不上传照片，请在正式入口使用拍照功能')
    return this.foodPhoto.recognize(image, signal)
  }

  async searchChineseFoods(query: string, page: number, signal: AbortSignal) {
    if (!this.chineseFood) throw new Error('演示模式不查询薄荷，请在正式入口使用')
    return this.chineseFood.search(query, page, signal)
  }
  async getChineseFood(code: string, signal: AbortSignal, query = '') {
    booheeCode.parse(code)
    signal.throwIfAborted()
    const cached = (await this.manualStore.listSavedFoods()).find(record => record.food.origin === 'boohee' && record.food.sourceId === code)
    signal.throwIfAborted()
    if (!cached && !this.chineseFood) throw new Error('演示模式不查询薄荷，请在正式入口使用')
    const food = foodSchema.parse(cached?.food ?? await this.chineseFood!.detail(code, signal))
    if (food.origin !== 'boohee' || food.sourceId !== code) throw new Error('食物资料不匹配，请重试')
    signal.throwIfAborted()
    const term = query.trim().slice(0, 30)
    try {
      await this.manualStore.putSavedFood({ id: food.id, food, createdAt: cached?.createdAt ?? new Date().toISOString(), libraryOnly: true, searchTerms: term ? [term] : [] })
    } catch { throw new Error('食物未能存入本机，请检查存储空间后重试') }
    signal.throwIfAborted()
    return food
  }

  async getIntakeHistory() { return this.manualStore.listIntakes() }
  async getSavedFoods() { return (await this.manualStore.listSavedFoods()).filter(record => !record.libraryOnly) }
  async searchFoods(query: string) { return searchFoodCatalog(query, await this.manualStore.listSavedFoods()) }
  async lookupFoodBarcode(barcode: string) {
    if (!this.foodLookup) throw new Error('条码查询暂不可用，可按标签自定义食物')
    return this.foodLookup.lookupBarcode(barcode)
  }
  async saveIntake(input: IntakeInput, id?: string, now = new Date()) {
    const existing = id ? (await this.manualStore.listIntakes()).find((r) => r.id === id) : undefined
    if (id && !existing) throw new Error('这条饮食记录已不存在，请重新打开')
    const record = createIntake(input, now, existing)
    await this.manualStore.putIntake(record)
    return record
  }
  async deleteIntake(id: string) { await this.manualStore.deleteIntake(id) }
  async saveIntakeMeal(inputs: IntakeInput[], now = new Date()) {
    if (!inputs.length || inputs.length > 20) throw new Error('一餐请记录1至20项食物')
    const records = inputs.map((input) => createIntake(input, now))
    if (records.some((r) => r.localDay !== records[0]!.localDay || r.meal !== records[0]!.meal || r.timeZone !== records[0]!.timeZone)) throw new Error('请保存到同一天的同一餐次')
    await this.manualStore.putIntakes(records)
    return records
  }
  async saveFood(food: Food, now = new Date()) {
    const parsed = foodSchema.parse(food)
    await this.manualStore.putSavedFood({ id: parsed.id, food: parsed, createdAt: now.toISOString() })
  }
  async deleteSavedFood(id: string) { await this.manualStore.deleteSavedFood(id) }

  async getMoodHistory() { return this.manualStore.listMoods() }
  async getPeriodHistory() { return this.manualStore.listPeriods() }

  async saveMood(localDay: string, timeZone: string, mood: MoodValue, note: string, now = new Date()) {
    validateJournalDay(localDay, timeZone, now)
    if ([...note.trim()].length > 200) throw new Error('最多写200字')
    const timestamp = now.toISOString()
    const record = moodRecordSchema.parse({ localDay, timeZone, mood, note: note.trim(),
      source: 'manual', createdAt: timestamp, updatedAt: timestamp })
    await this.manualStore.putMood(record)
  }

  async deleteMood(localDay: string) { await this.manualStore.deleteMood(localDay) }

  async setPeriod(start: string, end: string, timeZone: string, recorded: boolean, now = new Date()) {
    const days = periodDays(start, end, timeZone, now)
    const timestamp = now.toISOString()
    await this.manualStore.updatePeriods(recorded ? days.map((localDay) => ({
      localDay, timeZone, source: 'manual' as const, createdAt: timestamp, updatedAt: timestamp,
    })) : [], recorded ? [] : days)
  }

  async createMetric(input: ManualMetricInput, now: Date): Promise<MetricRecord> {
    const record = parseMetricInput(input, now)
    await this.manualStore.putMetric(record)
    return record
  }

  async updateMetric(id: string, input: ManualMetricInput, now: Date): Promise<MetricRecord> {
    const existing = await this.manualStore.getManualMetric(id)
    if (existing === null) throw new Error(READ_ONLY_ERROR)
    const parsed = parseMetricInput(input, now)
    const record = { ...parsed, id: existing.id, createdAt: existing.createdAt }
    await this.manualStore.putMetric(record)
    return record
  }

  async createSleep(input: ManualSleepInput, now: Date): Promise<SleepRecord> {
    const existing = await this.manualStore.listSleepHistory()
    const record = parseSleepInput(input, existing, now)
    await this.manualStore.putSleep(record)
    return record
  }

  async updateSleep(id: string, input: ManualSleepInput, now: Date): Promise<SleepRecord> {
    const [existing, history] = await Promise.all([
      this.manualStore.getManualSleep(id),
      this.manualStore.listSleepHistory(),
    ])
    if (existing === null) throw new Error(READ_ONLY_ERROR)
    const parsed = parseSleepInput(input, history, now, id)
    const record = { ...parsed, id: existing.id, createdAt: existing.createdAt }
    await this.manualStore.putSleep(record)
    return record
  }

  async createWorkout(input: ManualWorkoutInput, now: Date): Promise<WorkoutRecord> {
    const record = parseWorkoutInput(input, now)
    await this.manualStore.putWorkout(record)
    return record
  }

  async updateWorkout(id: string, input: ManualWorkoutInput, now: Date): Promise<WorkoutRecord> {
    const existing = await this.manualStore.getManualWorkout(id)
    if (existing === null) throw new Error(READ_ONLY_ERROR)
    const parsed = parseWorkoutInput(input, now)
    const record = { ...parsed, id: existing.id, createdAt: existing.createdAt }
    await this.manualStore.putWorkout(record)
    return record
  }

  async setWorkoutAppearance(record: WorkoutRecord, appearance: WorkoutAppearance | null): Promise<void> {
    if (record.source === 'manual') throw new Error('请使用手动训练编辑')
    const key = recordKey(record)
    const records = mergeRecords(await Promise.all(this.providers.map((provider) => provider.listWorkoutHistory())))
    if (!records.some((candidate) => recordKey(candidate) === key)) throw new Error('训练记录已不存在，请重新同步')
    if (appearance === null) await this.manualStore.deleteWorkoutOverride(key)
    else await this.manualStore.putWorkoutOverride({ ...workoutAppearanceSchema.parse(appearance), key })
  }

  private async applyWorkoutAppearance(records: WorkoutRecord[]): Promise<WorkoutRecord[]> {
    const overrides = new Map((await this.manualStore.listWorkoutOverrides()).map(({ key, ...appearance }) => [key, appearance]))
    return records.map((record) => record.source === 'manual' || !overrides.has(recordKey(record))
      ? record : { ...record, appearance: overrides.get(recordKey(record)) })
  }

  async deleteManual(kind: 'metric' | 'sleep' | 'workout', id: string): Promise<void> {
    const existing =
      kind === 'metric'
        ? await this.manualStore.getManualMetric(id)
        : kind === 'sleep'
          ? await this.manualStore.getManualSleep(id)
          : await this.manualStore.getManualWorkout(id)
    if (existing === null) throw new Error(READ_ONLY_ERROR)
    await this.manualStore.deleteManual(kind, id)
  }

  async getToday(referenceAt: string, timeZone: string): Promise<TodaySnapshot> {
    const range = localDayRange(referenceAt, timeZone, 2)
    const todayRange = localDayRange(referenceAt, timeZone, 1)
    const results = await Promise.all(
      this.providers.map(async (provider) => {
        const [metrics, sleeps, weight, bodyFat] = await Promise.all([
          provider.listMetrics(range),
          provider.listSleeps(todayRange),
          provider.listMetricHistory('weight'),
          provider.listMetricHistory('bodyFat'),
        ])
        return { metrics, sleeps, weight, bodyFat }
      }),
    )
    const metrics = mergeRecords(
      results.map(({ metrics, weight, bodyFat }) => [
        ...metrics,
        ...weight,
        ...bodyFat,
      ]),
    )
    const sleeps = mergeRecords(results.map(({ sleeps }) => sleeps))
    return selectTodaySnapshot(metrics, sleeps, referenceAt, timeZone)
  }

  async getMetricTrend(
    type: MetricType,
    days: number,
    referenceAt: string,
    timeZone: string,
  ): Promise<MetricTrendPoint[]> {
    const range = localDayRange(referenceAt, timeZone, days)
    const records = mergeRecords(await Promise.all(this.providers.map((provider) => provider.listMetrics(range))))
    return buildMetricTrend(records, type, days, referenceAt, timeZone)
  }

  async getSleepTrend(
    days: number,
    referenceAt: string,
    timeZone: string,
  ): Promise<MetricTrendPoint[]> {
    const range = localDayRange(referenceAt, timeZone, days)
    const records = mergeRecords(await Promise.all(this.providers.map((provider) => provider.listSleeps(range))))
    return buildSleepTrend(records, days, referenceAt, timeZone)
  }

  async listMetricHistory(type: MetricType): Promise<MetricRecord[]> {
    const records = mergeRecords(
      await Promise.all(this.providers.map((provider) => provider.listMetricHistory(type))),
    )
    return [...records].sort(metricDescending)
  }

  async listSleepHistory(): Promise<SleepRecord[]> {
    const records = mergeRecords(
      await Promise.all(this.providers.map((provider) => provider.listSleepHistory())),
    )
    return [...records].sort(sleepDescending)
  }

  async listWorkoutHistory(): Promise<WorkoutRecord[]> {
    const records = mergeRecords(
      await Promise.all(this.providers.map((provider) => provider.listWorkoutHistory())),
    )
    return this.applyWorkoutAppearance([...records].sort(workoutDescending))
  }

  getManualMetric(id: string): Promise<MetricRecord | null> {
    return this.manualStore.getManualMetric(id)
  }

  getManualSleep(id: string): Promise<SleepRecord | null> {
    return this.manualStore.getManualSleep(id)
  }

  getManualWorkout(id: string): Promise<WorkoutRecord | null> {
    return this.manualStore.getManualWorkout(id)
  }

  async getWorkoutMonth(monthKey: string, timeZone: string): Promise<WorkoutRecord[]> {
    const range = monthRange(monthKey, timeZone)
    const start = parseIsoInstant(range.startInclusive).getTime()
    const end = parseIsoInstant(range.endExclusive).getTime()
    return this.applyWorkoutAppearance(mergeRecords(
      await Promise.all(this.providers.map((provider) => provider.listWorkouts(range))),
    )
      .filter((record) => {
        const instant = parseIsoInstant(record.startedAt).getTime()
        return instant >= start && instant < end
      })
      .sort(workoutDescending))
  }

  async getWorkoutWeek(
    referenceAt: string,
    timeZone: string,
  ): Promise<{ summary: WorkoutWeekSummary; records: WorkoutRecord[] }> {
    const range = mondayWeekRange(referenceAt, timeZone)
    const start = parseIsoInstant(range.startInclusive).getTime()
    const end = parseIsoInstant(range.endExclusive).getTime()
    const records = mergeRecords(
      await Promise.all(this.providers.map((provider) => provider.listWorkouts(range))),
    )
      .filter((record) => {
        const instant = parseIsoInstant(record.startedAt).getTime()
        return instant >= start && instant < end
      })
      .sort(workoutDescending)
    return {
      records: await this.applyWorkoutAppearance(records),
      summary: summarizeWorkoutWeek(records, referenceAt, timeZone),
    }
  }
}
